import { FeatureFlagName } from '@/constants/featureFlags';
import { GET_FEATURE_FLAGS } from '@/gql/featureFlags';
import { featureFlagKeys } from '@/services/featureFlags';
import { fetchData } from '@/services/graphql';
import { useQuery } from '@tanstack/react-query';

interface GetFeatureFlagsResponse {
  featureFlags: Array<{
    id: string;
    name: string;
    isEnabled: boolean;
  }>;
}

interface FeatureFlagCacheItem {
  id: string;
  name: string;
  isEnabled: boolean;
  description?: string;
  updatedAt?: string;
  createdById?: string;
  updatedById?: string;
}

interface UseFeatureFlagReturn {
  isEnabled: boolean;
  isLoading: boolean;
  error: Error | null;
}

const useFeatureFlag = (flagName: FeatureFlagName, defaultValue: boolean = false): UseFeatureFlagReturn => {
  const { data, isLoading, error } = useQuery<FeatureFlagCacheItem[]>({
    queryKey: featureFlagKeys.lists(),
    queryFn: async () => {
      const { featureFlags } = await fetchData<GetFeatureFlagsResponse>(GET_FEATURE_FLAGS);
      return featureFlags.map((flag) => ({
        id: flag.id,
        name: flag.name,
        isEnabled: flag.isEnabled,
      }));
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const flag = data?.find((flag) => flag.name === flagName);
  const isEnabled = flag ? (typeof flag.isEnabled === 'boolean' ? flag.isEnabled : defaultValue) : defaultValue;

  return {
    isEnabled,
    isLoading,
    error: error as Error | null,
  };
};

export default useFeatureFlag;
